import { GetTransactionsModel, IServiceTransactionManager, Listing, ServiceTransaction, ServiceTransactionType } from '@truckdown/locations';
import { Constants, ServiceModel } from '@truckdown/systems';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted } from 'vue';

const item_per_page = 15;

interface ILastKey {
    lastId?: string,
    lastModified?: Date
}

interface IItem {
    messages: string[],
    listing: Listing,
    model: ServiceTransaction
}

interface IData {
    list: Ref<IItem[]>,
    total: Ref<number>,
    type: Ref<string>,
    loading: Ref<boolean>,
    pages: Ref<ILastKey[]>,
    current: Ref<ILastKey>
}

const getData = function () {
    return {
        list: ref<IItem[]>([]),
        total: ref<number>(0),
        type: ref<string>('user'),
        loading: ref<boolean>(false),
        pages: ref<ILastKey[]>([]),
        current: ref<ILastKey>({})
    } as IData;
};

const getComputed = function (data: IData) {
    return {
        pagerText: computed(() => {
            let previous = (data.pages.value.length * item_per_page);
            let start = previous + 1;
            let end = previous + data.list.value.length;
            return 'Displaying ' + start + ' to ' + end + ' of ' + data.total.value;
        }),
        enablePrevious: computed(() => {
            return !data.loading.value && data.pages.value.length > 0;
        }),
        enableNext: computed(() => {
            let previous = (data.pages.value.length * item_per_page);
            return !data.loading.value && (previous + data.list.value.length) < data.total.value;
        })
    };
}

const getMessages = function (model: ServiceTransaction) {
    var messages: string[] = [];

    if (model.users.length > 0) {
        var createdMessage = model.type == ServiceTransactionType.Request ? 'Request submitted by ' : 'Numbers shown by ';
        messages.push(createdMessage + model.users[0].user + ' on ' + new Date(model.users[0].updated).toLocaleDateString("en-US", {
            weekday: 'short',
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }));

        if (model.lastUpdated != model.users[0].updated) {
            messages.push('Last updated by ' + model.users[model.users.length - 1].user + ' on ' + new Date(model.lastUpdated).toLocaleDateString("en-US", {
                weekday: 'short',
                month: 'short',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }));
        }
    }

    return messages;
}

const getItem = function (model: ServiceTransaction): IItem {
    return {
        messages: getMessages(model),
        listing: model.listing,
        model: model
    }
}

const runSearch = function (data: IData, lastKey?: ILastKey) {
    data.loading.value = true;
    context.getService<IServiceTransactionManager>('IServiceTransactionManager')
        .then((service) => {
            return service.getTransactions({
                showAll: data.type.value == 'account',
                limit: item_per_page,
                lastId: lastKey?.lastId,
                lastModified: lastKey?.lastModified
            } as GetTransactionsModel)
                .then((res) => {
                    if (lastKey) {
                        if (lastKey.lastId == data.list.value[data.list.value.length - 1].model.id) {
                            //next
                            data.pages.value.push(data.current.value);
                        } else if (lastKey.lastId != data.current.value.lastId) {
                            //previous
                            data.pages.value.pop();
                        }
                        data.current.value = lastKey;
                    } else {
                        data.pages.value = [];
                        data.current.value = {};
                    }
                    data.total.value = res.count;
                    data.list.value = res.results
                        .map((m) => { return getItem(m); });
                });
        })
        .catch((reason) => {
            console.log('Error getting search history', reason);
            data.total.value = 0;
            data.list.value = [];
        })
        .finally(() => {
            data.loading.value = false;
        });
}

const getMethods = function (data: IData) {
    return {
        onPrevious: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastKey = data.pages.value[data.pages.value.length - 1];
                runSearch(data, lastKey);
            }
        },
        onNext: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastListing = data.list.value[data.list.value.length - 1];
                let lastKey = {
                    lastModified: new Date(lastListing.model.lastUpdated),
                    lastId: lastListing.model.id
                } as ILastKey;
                runSearch(data, lastKey);
            }
        },
        changeType: function (ev: Event) {
            runSearch(data);
        },
        viewListing: function (model: ServiceTransaction) {
            let name = encodeURIComponent(model.listing.name.replace(/\s/g, '_').toLowerCase());
            let url = '/search/view-detail/' + name + '/' + model.locationId;
            window.location.href = url;
        }
    };
}

export default defineComponent({
    props: {
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData();
        let computed = getComputed(data);
        let methods = getMethods(data);

        onMounted(() => {
            runSearch(data);
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
